import { route } from 'quasar/wrappers';
import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  createWebHistory,
} from 'vue-router';

import routes from './routes';
import { useAuthStore } from 'stores/auth-store';
import { usePermissionsStore } from 'stores/permissions-store';
import { useQueryCache } from '@pinia/colada';
import { FeatureFlags, FeatureName } from 'src/services/feature-flags';
import { isEnabledAnywhere } from 'src/utils/featureFlags';
import { featureFlagsQuery } from 'src/queries/feature-flags/useFetchFeatureFlagsQuery';

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default route(function (/* { store, ssrContext } */) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === 'history'
    ? createWebHistory
    : createWebHashHistory;

  const Router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(process.env.VUE_ROUTER_BASE),
  });

  Router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();
    const { isLoggedIn } = authStore;

    // Allow access to public auth routes
    if (to.path.includes('/feature-flags')) {
      return next();
    }

    if (to.path.includes('/auth')) {
      // If already logged in, redirect to home instead of showing auth pages
      if (isLoggedIn() && to.path.includes('/auth/login')) {
        return next('/');
      }
      return next();
    }

    // Allow access to access-denied page
    if (to.path === '/access-denied') {
      return next();
    }

    // If not logged in and trying to access protected route, redirect to login
    if (!isLoggedIn()) {
      return next('/auth/login');
    }

    // Check if feature flag requirements are met
    const flag = to.meta.requiredFeatureFlag as FeatureName | undefined;
    if (flag) {
      const queryCache = useQueryCache();
      let flags: FeatureFlags | undefined;

      try {
        // `ensure` registers the query, and `refresh` honours its staleTime — so
        // a warm cache costs no request — then resolves with the entry's state.
        const state = await queryCache.refresh(
          queryCache.ensure(featureFlagsQuery)
        );
        flags = state.data;
      } catch {
        // `refresh` rethrows a failed request. Swallowing it here leaves `flags`
        // undefined for the check below, rather than abandoning the navigation.
      }

      // Fail closed. A flag we could not read is not a flag we hold, and the API
      // omits the key altogether for a user it has refused the feature — so
      // letting navigation through would only render a screen whose every
      // request 404s.
      if (!flags || !isEnabledAnywhere(flag, flags)) {
        return next('/page-not-found');
      }
    }

    // Only check permissions if user is logged in and permissions are loaded
    const permissionsStore = usePermissionsStore();

    if (to.meta.can) {
      // Skip permission check if permissions haven't loaded yet
      // They will be checked again when the user navigates
      if (
        !permissionsStore.permissions ||
        !Array.isArray(permissionsStore.permissions) ||
        permissionsStore.permissions.length === 0
      ) {
        // Permissions not loaded yet - allow navigation, component-level checks will handle it
        return next();
      }

      // meta.can may be a single permission key or an array of keys. An array
      // is satisfied when the user holds ANY one of the listed permissions (OR).
      const requiredPermissions = Array.isArray(to.meta.can)
        ? (to.meta.can as string[])
        : [to.meta.can as string];

      const scope: { region_id?: number; unit_id?: number } = {};
      if (to.params.region_id) {
        scope.region_id = parseInt(to.params.region_id as string);
      }
      if (to.params.unit_id) {
        scope.unit_id = parseInt(to.params.unit_id as string);
      }

      const hasAccess = requiredPermissions.some((can) =>
        permissionsStore.can({ can, ...scope })
      );

      if (!hasAccess) {
        return next('/access-denied');
      }
    }

    // User is logged in, allow access
    return next();
  });

  return Router;
});
